import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { WebSocketSubscribe } from 'api/event-state';
import { EventWsClient } from 'lib/presentation-ws-client';
import { pickInitialSelectedSegmentId, findActiveSegmentId, pickSelectedSegmentIdOnActiveChange } from 'lib/event-state-merge';
import { isTransportOnline } from 'lib/ws-heartbeat';
import { persistOwnConnectionMicFromSnapshot, readPreferredAudioSourceDeviceId, } from 'lib/audio-source-storage';
const EventAdminStateContext = createContext(null);
export const useEventAdminState = () => {
    const ctx = useContext(EventAdminStateContext);
    if (ctx === null) {
        throw new Error('useEventAdminState must be used within EventStateProvider');
    }
    return ctx;
};
export const EventStateProvider = ({ eventId, children }) => {
    const [eventSnapshot, setEventSnapshot] = useState(null);
    const [eventLiveTelemetry, setEventLiveTelemetry] = useState(null);
    const [selectedSegmentId, setSelectedSegmentId] = useState(null);
    const [connectionReady, setConnectionReady] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [connectionHealth, setConnectionHealth] = useState('connecting');
    const [actionWaiting, setActionWaiting] = useState(false);
    const [connectionId, setConnectionId] = useState(null);
    const [connectionNumber, setConnectionNumber] = useState(null);
    const [wsClient, setWsClient] = useState(null);
    const previousActiveSegmentIdRef = useRef(undefined);
    const clearActionWaiting = useCallback(() => {
        setActionWaiting(false);
    }, []);
    const applySnapshotSelection = useCallback((snapshot) => {
        const activeSegmentId = findActiveSegmentId(snapshot);
        const previousActiveSegmentId = previousActiveSegmentIdRef.current;
        if (previousActiveSegmentId === undefined) {
            setSelectedSegmentId(pickInitialSelectedSegmentId(snapshot));
        }
        else if (activeSegmentId !== previousActiveSegmentId) {
            setSelectedSegmentId((selectedSegmentId) => {
                const nextSelected = pickSelectedSegmentIdOnActiveChange(previousActiveSegmentId, activeSegmentId, selectedSegmentId);
                return nextSelected !== undefined ? nextSelected : selectedSegmentId;
            });
        }
        previousActiveSegmentIdRef.current = activeSegmentId;
    }, []);
    useEffect(() => {
        previousActiveSegmentIdRef.current = undefined;
        setEventSnapshot(null);
        setEventLiveTelemetry(null);
        setSelectedSegmentId(null);
        setConnectionReady(false);
        setWsConnected(false);
        setConnectionHealth('connecting');
        setActionWaiting(false);
        setConnectionId(null);
        setConnectionNumber(null);
        const client = new EventWsClient({
            eventId,
            subscribe: [WebSocketSubscribe.EventSnapshot, WebSocketSubscribe.EventLiveTelemetry],
            module: true,
            admin: true,
            preferredAudioSourceDeviceId: readPreferredAudioSourceDeviceId() ?? undefined,
        });
        setWsClient(client);
        client.connect({
            admin: {
                onEventSnapshot: (snapshot) => {
                    setEventSnapshot(snapshot);
                    clearActionWaiting();
                    applySnapshotSelection(snapshot);
                },
                onEventLiveTelemetry: (telemetry) => {
                    setEventLiveTelemetry(telemetry);
                },
                onEventConnectionAssigned: (payload) => {
                    setConnectionId(payload.connectionId);
                    setConnectionNumber(payload.connectionNumber);
                },
            },
            onOpen: () => {
                setConnectionReady(true);
                setWsConnected(true);
            },
            onClose: () => {
                setWsConnected(false);
                setConnectionId(null);
                setConnectionNumber(null);
            },
            onHealthChange: (health) => {
                setConnectionHealth(health);
            },
        });
        return () => {
            client.disconnect();
            setWsClient(null);
        };
    }, [eventId, clearActionWaiting, applySnapshotSelection]);
    useEffect(() => {
        if (eventSnapshot !== null && connectionId !== null)
            persistOwnConnectionMicFromSnapshot(eventSnapshot, connectionId);
    }, [eventSnapshot, connectionId]);
    const transportOnline = isTransportOnline(connectionHealth);
    const value = useMemo(() => ({
        eventSnapshot,
        eventLiveTelemetry,
        selectedSegmentId,
        setSelectedSegmentId,
        connectionReady,
        wsConnected,
        connectionHealth,
        transportOnline,
        actionWaiting,
        setActionWaiting,
        wsClient,
        connectionId,
        connectionNumber,
    }), [
        eventSnapshot,
        eventLiveTelemetry,
        selectedSegmentId,
        connectionReady,
        wsConnected,
        connectionHealth,
        transportOnline,
        actionWaiting,
        wsClient,
        connectionId,
        connectionNumber,
    ]);
    return (_jsx(EventAdminStateContext.Provider, { value: value, children: children }));
};
